import { RouteRecordRaw } from 'vue-router';
import { IRouteMeta, ResolveParams } from '@/core/models/routing';
import { AppCoreEnums } from '@/core/shared/enums';
import useCoreUtils from '@/core/composable/use-core-utils';
import { Index } from '@/stores/indexes/models';
import useIndexUtils from '@/composables/indexes/use-index-utils';
import { useIndexesStore } from '@/stores';
import { AppPermissions } from '@/shared/permissions';

const ROUTE_INDEXES_ROOT = 'indexes-root';
const ROUTE_INDEXES = 'indexes';
const ROUTE_INDEX_DETAILS = 'index-details';
const ROUTE_INDEX_DOSSIER = 'index-dossier';
const ROUTE_INDEX_PROPERTIES = 'index-properties';
const ROUTE_INDEX_ADVANCED = 'index-advanced';
const { propertyOf } = useCoreUtils();

const indexesRoutes: RouteRecordRaw = {
  name: ROUTE_INDEXES_ROOT,
  path: ROUTE_INDEXES,
  component: () => import('@/core/containers/empty-container.vue'),
  meta: <IRouteMeta>{
    containerType: AppCoreEnums.ContainerType.EmptyContainer,
    permissions: { readPermission: AppPermissions.IndexesRead },
  },
  redirect: { name: ROUTE_INDEXES },
  children: [
    {
      name: ROUTE_INDEXES,
      path: 'list',
      component: () => import('@/pages/indexes/indexes-page.vue'),
      meta: <IRouteMeta>{
        collapsibleTableTitleField: propertyOf<Index>('name'),
        resolve: async (resolveParams: ResolveParams) => {
          const { useIndexesStore, useUsersStore } = await import('@/stores');
          return Promise.all([
            useIndexesStore().getIndexes(resolveParams),
            useIndexesStore().getIndexesIds(),
            useUsersStore().loadAllUsersLite(),
          ]);
        },
        permissions: { readPermission: AppPermissions.IndexesRead },
        containerType: AppCoreEnums.ContainerType.CollapsibleTableContainer,
      },
      children: [
        {
          name: ROUTE_INDEX_DETAILS,
          path: ':id',
          component: () => import('@/core/pages/app-tabs-container-page.vue'),
          meta: <IRouteMeta>{
            containerType: AppCoreEnums.ContainerType.TabsContainer,
            resolve: async (resolveParams: ResolveParams) => {
              const { useIndexesStore } = await import('@/stores');
              return useIndexesStore().getIndexById(resolveParams);
            },
            permissions: { readPermission: AppPermissions.IndexesRead, writePermission: AppPermissions.IndexesWrite },
            tabs: [
              {
                label: 'core.properties',
                to: ROUTE_INDEX_PROPERTIES,
              },
              {
                label: 'indexes.advanced',
                to: ROUTE_INDEX_ADVANCED,
              },
              {
                label: 'indexes.dossier',
                to: ROUTE_INDEX_DOSSIER,
              },
            ],
          },
          redirect: { name: ROUTE_INDEX_PROPERTIES },
          children: [
            {
              name: ROUTE_INDEX_PROPERTIES,
              path: ROUTE_INDEX_PROPERTIES,
              component: () => import('@/core/pages/app-form-container-page.vue'),
              meta: <IRouteMeta>{
                containerType: AppCoreEnums.ContainerType.FormsContainer,
                formComponent: () => import('@/forms/indexes/index-properties-form.vue'),
                formModel: () => useIndexesStore().get('IndexProperties'),
                formSave: () => useIndexesStore().saveIndex,
                resolve: async (resolveParams: ResolveParams) => {
                  if (!resolveParams.useStoreData) return Promise.resolve();
                  const { useIndexesStore } = await import('@/stores');
                  return useIndexesStore().getIndexById(resolveParams);
                },
                permissions: { readPermission: AppPermissions.IndexesRead, writePermission: AppPermissions.IndexesWrite },
              },
            },
            {
              name: ROUTE_INDEX_ADVANCED,
              path: ROUTE_INDEX_ADVANCED,
              component: () => import('@/core/pages/app-form-container-page.vue'),
              meta: <IRouteMeta>{
                containerType: AppCoreEnums.ContainerType.FormsContainer,
                formComponent: () => import('@/forms/indexes/index-advanced-form.vue'),
                formModel: () => useIndexesStore().get('IndexProperties'),
                formSave: () => useIndexesStore().saveIndex,
                resolve: async (resolveParams: ResolveParams) => {
                  if (!resolveParams.useStoreData) return Promise.resolve();
                  const { useIndexesStore } = await import('@/stores');
                  return useIndexesStore().getIndexById(resolveParams);
                },
                permissions: { readPermission: AppPermissions.IndexesRead },
              },
            },
            {
              name: ROUTE_INDEX_DOSSIER,
              path: ROUTE_INDEX_DOSSIER,
              component: () => import('@/pages/indexes/index-dossier-page.vue'),
              meta: <IRouteMeta>{
                containerType: AppCoreEnums.ContainerType.EmptyContainer,
                resolve: (resolveParams: ResolveParams) => useIndexUtils().loadIndexDossier(resolveParams),
                permissions: { readPermission: AppPermissions.IndexesRead },
              },
            },
          ],
        },
      ],
    },
  ],
};

export {
  indexesRoutes,
  ROUTE_INDEXES,
  ROUTE_INDEXES_ROOT,
  ROUTE_INDEX_DOSSIER,
  ROUTE_INDEX_PROPERTIES,
  ROUTE_INDEX_ADVANCED,
  ROUTE_INDEX_DETAILS,
};
